import { Equipment } from "@domain/library/equipment";
import { getEnumValues } from "@utils/enum-utils";
import _ from "lodash";

export interface ExerciseComment {
	text: string;
	lastUpdateDate: Date;
	lastEditorName: string;
}

interface ExerciseCommon {
	id: string;
	draggableId: string;
	revision: number;
	title: string;
	description?: string;
	keyframeUrls: string[];
	comment?: ExerciseComment;
	thumbnails: {
		smallThumbnailUrl?: string;
		mediumThumbnailUrl?: string;
		largeThumbnailUrl?: string;
	};
	videoUrl?: string;
	audioUrl?: string;
	units: number;
	setCount: number;
	helpNeeded: boolean;
	requiredEquipments: Equipment[];
	optionalEquipments: Equipment[];
	category: ExerciseCategory;
	primaryBodyPart: ExerciseBodyPart;
	secondaryBodyParts: ExerciseBodyPart[];
	startingPosition: ExerciseStartingPosition;
	difficulty: ExerciseDifficulty;
	tags: string[];
	withWheelchair: boolean;
	patientWarning?: string;
}

export enum ExerciseType {
	Duration = "DURATION",
	Repetition = "REPETITION",
}

export enum ExerciseCategory {
	WarmUp = "Warm-up",
	Stretching = "Stretching",
	Strengthening = "Strengthening",
	CoordinationAndBalance = "Coordination and balance",
}

export const exerciseCategories = getEnumValues(ExerciseCategory);

export enum ExerciseBodyPart {
	FullBody = "Full body",
	HeadTrunk = "Head & trunk",
	UpperLimbs = "Upper limbs",
	LowerLimbs = "Lower limbs",
}

export const exerciseBodyPartsValues = getEnumValues(ExerciseBodyPart);

export enum ExerciseStartingPosition {
	LyingDown = "Lying down",
	Kneeling = "Kneeling",
	SittingChair = "Sitting (chair)",
	SittingFloor = "Sitting (floor)",
	Standing = "Standing",
}

export const exerciseStartingPositions = getEnumValues(ExerciseStartingPosition);

export enum ExerciseDifficulty {
	Easy = "Easy",
	Moderate = "Moderate",
	Hard = "Hard",
}

export const exerciseDifficulties = getEnumValues(ExerciseDifficulty);

export enum ExercisePatientWarning {
	No = "No",
	AtaxiaWarning = "Ataxia Warning"
}

export const isExerciseCategory = (value: string): value is ExerciseCategory =>
	exerciseCategories.includes(value as ExerciseCategory);

export const isExerciseBodyPart = (value: string): value is ExerciseBodyPart =>
	exerciseBodyPartsValues.includes(value as ExerciseBodyPart);

export const isExerciseStartingPosition = (value: string): value is ExerciseStartingPosition =>
	exerciseStartingPositions.includes(value as ExerciseStartingPosition);

export const isExerciseDifficulty = (value: string): value is ExerciseDifficulty =>
	exerciseDifficulties.includes(value as ExerciseDifficulty);

interface RepetitionExercise extends ExerciseCommon {
	unitType: ExerciseType.Repetition;
}

interface DurationExercise extends ExerciseCommon {
	unitType: ExerciseType.Duration;
}

export type BaseExercise = DurationExercise | RepetitionExercise;

export type MainExercise = BaseExercise & { alternativeExercises: BaseExercise[] };

export type Exercise = MainExercise | BaseExercise;

export const isMainExercise = (exercise: Exercise): exercise is MainExercise => "alternativeExercises" in exercise;

export const exerciseBodyParts = (exercise: Exercise): ExerciseBodyPart[] => [
	exercise.primaryBodyPart,
	...exercise.secondaryBodyParts,
];

export const REPETITION_TIME_IN_SEC = 10;

export function exerciseContentAreEquals(ex1: Exercise, ex2: Exercise): boolean {
	const ex1Props = _.omit(ex1, "draggableId");
	const ex2Props = _.omit(ex2, "draggableId");
	return JSON.stringify(ex1Props) === JSON.stringify(ex2Props);
}

export function isDurationExercise(exercise: Exercise): exercise is DurationExercise {
	return exercise.unitType === ExerciseType.Duration;
}
